import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { 
  Search, 
  Home, 
  Building2, 
  FileSpreadsheet, 
  FileText, 
  BarChart3, 
  TrendingUp,
  ClipboardCheck,
  Shield,
  Settings,
  Upload,
  HelpCircle,
  Command
} from 'lucide-react';

const commands = [
  { id: 'dashboard', label: 'Dashboard', path: '/dashboard', icon: Home, shortcut: 'g d' },
  { id: 'accounts', label: 'Accounts', path: '/accounts', icon: Building2, shortcut: 'g a' },
  { id: 'trial-balance', label: 'Trial Balance', path: '/accounts?tab=trial-balance', icon: FileSpreadsheet, shortcut: 'g t' },
  { id: 'balance-sheet', label: 'Balance Sheet', path: '/balance-sheet', icon: FileSpreadsheet, shortcut: 'g b' },
  { id: 'income-statement', label: 'Income Statement', path: '/income-statement', icon: FileText, shortcut: 'g i' },
  { id: 'mda', label: 'MD&A', path: '/mda', icon: FileText, shortcut: 'g m' },
  { id: 'analytics', label: 'Analytics', path: '/analytics', icon: BarChart3, shortcut: 'g n' },
  { id: 'fpa', label: 'FP&A', path: '/fpa', icon: TrendingUp, shortcut: 'g f' },
  { id: 'reconciliations', label: 'Reconciliations', path: '/reconciliations', icon: ClipboardCheck, shortcut: 'g r' },
  { id: 'audit-trail', label: 'Audit Trail', path: '/audit-trail', icon: Shield, shortcut: 'g u' },
  { id: 'import', label: 'Import Data', path: '/import', icon: Upload, shortcut: 'g p' },
  { id: 'settings', label: 'Settings', path: '/settings', icon: Settings, shortcut: 'g s' },
  { id: 'help', label: 'Help & FAQ', path: '/faq', icon: HelpCircle, shortcut: '?' }
];

export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const down = (e) => {
      // Cmd+K or Ctrl+K to open command palette
      if ((e.key === 'k' && (e.metaKey || e.ctrlKey)) || e.key === '/') {
        e.preventDefault();
        setOpen(true);
      }
      
      // Escape to close
      if (e.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);

  // Navigation shortcuts (when palette is closed)
  useEffect(() => {
    if (open) return;

    let sequence = '';
    let timeout;

    const handleKeyDown = (e) => {
      // Clear sequence after 1 second of inactivity
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        sequence = '';
      }, 1000);

      // Build sequence
      sequence += e.key.toLowerCase();

      // Find matching command
      const command = commands.find(cmd => cmd.shortcut === sequence);
      if (command) {
        e.preventDefault();
        navigate(command.path);
        sequence = '';
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      clearTimeout(timeout);
    };
  }, [open, navigate]);

  const filteredCommands = commands.filter(cmd => 
    cmd.label.toLowerCase().includes(search.toLowerCase()) ||
    cmd.shortcut.includes(search.toLowerCase())
  );

  const handleSelect = (path) => {
    navigate(path);
    setOpen(false);
    setSearch('');
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="p-0 overflow-hidden max-w-2xl">
        <div className="flex items-center border-b px-4 py-3">
          <Search className="w-4 h-4 text-muted-foreground mr-2" />
          <Input
            placeholder="Search commands or type a shortcut..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
            autoFocus
          />
          <Badge variant="outline" className="ml-2">
            <Command className="w-3 h-3 mr-1" />K
          </Badge>
        </div>

        <div className="max-h-[400px] overflow-y-auto p-2">
          {filteredCommands.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              No commands found
            </div>
          ) : (
            <div className="space-y-1">
              {filteredCommands.map((cmd) => {
                const Icon = cmd.icon;
                return (
                  <button
                    key={cmd.id}
                    onClick={() => handleSelect(cmd.path)}
                    className="w-full flex items-center justify-between px-3 py-2 rounded-md hover:bg-accent transition-colors text-left"
                  >
                    <div className="flex items-center gap-3">
                      <Icon className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm font-medium">{cmd.label}</span>
                    </div>
                    <Badge variant="secondary" className="text-xs font-mono">
                      {cmd.shortcut}
                    </Badge>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="border-t px-4 py-2 text-xs text-muted-foreground bg-muted/50">
          <div className="flex items-center justify-between">
            <span>Press <kbd className="px-1.5 py-0.5 rounded bg-background border">Cmd/Ctrl</kbd> + <kbd className="px-1.5 py-0.5 rounded bg-background border">K</kbd> or <kbd className="px-1.5 py-0.5 rounded bg-background border">/</kbd> to open</span>
            <span>Press <kbd className="px-1.5 py-0.5 rounded bg-background border">?</kbd> for help</span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
